import type { CardData } from '../types';
import { convertLtToMinutes } from './calculations';

export interface YamazumiKpiRow {
  name: string;
  trabalho: number;
  espera: number;
  disponibilidade: number;
  ocupacao: number;
}

const SHIFT_MINUTES = 480;
const MAX_OCCUPATION_PCT = 999;
const MAX_MINUTES = 1_000_000;

export function sanitizeMetricNumber(value: unknown, fallback = 0, max = MAX_MINUTES): number {
  const num = Number(value);
  if (!Number.isFinite(num) || Number.isNaN(num)) return fallback;
  return Math.min(max, Math.max(0, num));
}

export function clampOccupationPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_OCCUPATION_PCT, Math.max(0, value));
}

/** Normaliza demanda do card inicial para unidades/dia. */
export function normalizeDailyDemand(rawEntrada: number, unit?: string): number {
  const entrada = sanitizeMetricNumber(rawEntrada, 0, 1_000_000);
  if (entrada <= 0) return 0;
  if (unit === '/hora') return entrada * 8;
  if (unit === '/ms') return entrada / 20;
  return entrada;
}

/**
 * Carga agregada por função (cargo): soma TRA + Espera de todas as etapas
 * atribuídas ao recurso. Não representa etapa isolada — ver metrics.realBottleneck.
 */
export function buildYamazumiKpiData(
  cards: CardData[],
  taktTime: number,
  shiftMinutes = SHIFT_MINUTES,
): YamazumiKpiRow[] {
  const loadMap = new Map<string, { trabalho: number; espera: number }>();

  for (const card of cards) {
    if (card.tipo !== 'atividade' || card.status === 'removido' || card.status === 'deletado') continue;

    const name = (card.cargo || card.responsavel || card.quem || 'Não Atribuído').trim();
    const trabalho = sanitizeMetricNumber(card.tt, 0);
    const tp = convertLtToMinutes(Number(card.lt) || 0, card.tpUnit);
    const espera = Math.max(0, sanitizeMetricNumber(tp, 0) - trabalho);

    const current = loadMap.get(name) ?? { trabalho: 0, espera: 0 };
    current.trabalho += trabalho;
    current.espera += espera;
    loadMap.set(name, current);
  }

  const referenceMinutes = taktTime > 0.05 ? taktTime : shiftMinutes;

  return Array.from(loadMap.entries())
    .map(([name, data]) => {
      const trabalho = sanitizeMetricNumber(data.trabalho, 0);
      const espera = sanitizeMetricNumber(data.espera, 0);
      const total = trabalho + espera;
      const ocupacao = clampOccupationPercent((total / Math.max(referenceMinutes, 0.05)) * 100);

      return {
        name,
        trabalho,
        espera,
        disponibilidade: referenceMinutes,
        ocupacao,
      };
    })
    .sort((a, b) => (b.trabalho + b.espera) - (a.trabalho + a.espera));
}

export function resolveTaktTime(dailyDemand: number, shiftMinutes = SHIFT_MINUTES): number {
  const demand = sanitizeMetricNumber(dailyDemand, 0, 1_000_000);
  if (demand <= 0) return 0;
  return shiftMinutes / demand;
}
